
import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useFavorites } from '@/context/FavoritesContext';
import { Link } from 'react-router-dom';
import { Exhibit } from '@/data';
import { Heart } from 'lucide-react';

interface ExhibitCardProps {
  exhibit: Exhibit;
  showFavoriteButton?: boolean;
}

const ExhibitCard = ({ exhibit, showFavoriteButton = true }: ExhibitCardProps) => {
  const { language } = useLanguage();
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  
  const handleFavoriteToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isFavorite(exhibit.id)) {
      removeFavorite(exhibit.id);
    } else {
      addFavorite(exhibit.id);
    }
  };
  
  // Generate a consistent color for each exhibit based on its ID
  const colors = ['bg-museum-shapes-red', 'bg-museum-shapes-blue', 'bg-museum-shapes-green', 
                 'bg-museum-shapes-yellow', 'bg-museum-shapes-purple', 'bg-museum-shapes-orange'];
  
  // Fix: Use modulo directly since we're certain exhibit.id is a number per the interface
  const colorIndex = exhibit.id % colors.length;
  const cardColor = colors[colorIndex];
  
  return (
    <Link to={`/exhibit/${exhibit.id}`} className="museum-card block relative group">
      <div className="card-corner card-corner-tl"></div>
      <div className="relative h-40 overflow-hidden">
        <div className={`absolute inset-0 ${cardColor} opacity-30 transition-opacity duration-300 group-hover:opacity-0`}></div>
        <img 
          src={exhibit.image} 
          alt={language === 'en' ? exhibit.titleEn : exhibit.titleHe} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute bottom-0 w-full bg-gradient-to-t from-black/70 to-transparent p-3">
          <div className="absolute top-2 left-2 bg-white/80 backdrop-blur-sm rounded px-2 py-1 shadow-sm">
            <span className="text-xs font-medium">{exhibit.numberCode}</span>
          </div>
        </div>
      </div>
      <div className="p-4">
         <h3 className="font-display font-bold text-lg mb-2 whitespace-pre-line">
           {language === 'en' ? exhibit.titleEn : exhibit.titleHe}
         </h3>
        <p className="text-sm text-gray-600 line-clamp-2">
          {language === 'en' ? exhibit.descriptionEn : exhibit.descriptionHe}
        </p>
      </div>
      
      {showFavoriteButton && (
        <button 
          onClick={handleFavoriteToggle}
          className="absolute top-2 right-2 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-md transition-transform duration-300 hover:scale-110 hover:bg-white"
          aria-label={isFavorite(exhibit.id) ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart 
            className={`h-5 w-5 ${isFavorite(exhibit.id) ? 'fill-museum-shapes-red text-museum-shapes-red' : 'text-gray-600'}`} 
          />
        </button>
      )}
    </Link>
  );
};

export default ExhibitCard;
